import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatMoney } from '../../lib/format';
import type { ApprovalFrame } from '../../lib/ws';
import { Isolated } from './Bdi';
import { BrandMark } from './BrandMark';
import { Button } from './Button';
import { Check } from './icons';

export type ApprovalCardState = 'pending' | 'approved' | 'cancelled' | 'expired' | 'failed';

/** Lines of the preview shown before "Show all". */
const PREVIEW_LINES = 12;

/**
 * TWO's signature component: one concrete action that needs a yes. Everything the person needs
 * to decide is on the card. The only TWO Blue on the card is the primary button, labelled with
 * the action (or the amount when there is one). After approval the card becomes a done
 * checklist; cancelled and expired cards say so quietly.
 */
export function ApprovalCard({
  approval,
  onApprove,
  onCancel,
  busy = false,
  state = 'pending',
  checklist = [],
}: {
  approval: Omit<ApprovalFrame, 'type'>;
  onApprove: () => void | Promise<void>;
  onCancel: () => void | Promise<void>;
  busy?: boolean;
  state?: ApprovalCardState;
  /** Done-state items, e.g. "Event updated", "Update sent to alex@…". */
  checklist?: readonly string[];
}) {
  const { t, i18n } = useTranslation();
  const [localBusy, setLocalBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const locale = i18n.resolvedLanguage ?? 'en';
  const amount =
    approval.amount && approval.currency
      ? formatMoney(approval.amount, approval.currency, locale)
      : null;

  const rows: [string, string | undefined][] = [
    [t('approval.route'), approval.route],
    [t('approval.dates'), approval.dates],
    [
      t('approval.attendees'),
      approval.attendees?.length ? approval.attendees.join(', ') : undefined,
    ],
    [
      t('approval.recipients'),
      approval.recipients?.length ? approval.recipients.join(', ') : undefined,
    ],
    [t('approval.subject'), approval.subject],
    [t('approval.class'), approval.class],
    [t('approval.price'), amount ?? undefined],
    [
      t('approval.payment'),
      approval.payment_method_last4 ? `•••• ${approval.payment_method_last4}` : undefined,
    ],
    [t('approval.cancellation'), approval.cancellation_terms],
  ];

  const previewLines = approval.preview ? approval.preview.split('\n') : [];
  const previewLong = previewLines.length > PREVIEW_LINES;
  const preview =
    previewLong && !expanded ? previewLines.slice(0, PREVIEW_LINES).join('\n') : approval.preview;

  const headerKey =
    state === 'approved'
      ? 'approval.doneLabel'
      : state === 'cancelled'
        ? 'approval.cancelledLabel'
        : state === 'expired'
          ? 'approval.expiredLabel'
          : state === 'failed'
            ? 'approval.failedLabel'
            : 'approval.label';
  const primaryLabel = amount
    ? t('approval.approveAmount', { amount })
    : t(`approval.actions.${approval.action_label}`);

  return (
    <section
      aria-label={t('approval.label')}
      className="rounded-lg border border-line bg-surface p-4 shadow-sm dark:bg-elevated"
    >
      <div className="flex items-center gap-2 text-xs font-medium text-ink-2">
        <BrandMark size={16} />
        {t(headerKey)}
      </div>
      <p className="mt-2 text-lg font-medium text-ink" dir="auto">
        <Isolated text={approval.item} />
      </p>

      {state === 'approved' ? (
        <ul className="mt-3 flex flex-col gap-2">
          {checklist.map((line) => (
            <li key={line} className="flex items-center gap-2 text-sm text-ink">
              <Check size={16} className="text-success" strokeWidth={2.5} />
              <span dir="auto">
                <Isolated text={line} />
              </span>
            </li>
          ))}
        </ul>
      ) : state === 'cancelled' ? (
        <p className="mt-3 text-sm text-ink-2" dir="auto">
          {t('approval.cancelledBody')}
        </p>
      ) : state === 'expired' ? (
        <p className="mt-3 text-sm text-ink-2" dir="auto">
          {t('approval.expiredBody')}
        </p>
      ) : state === 'failed' ? (
        <p className="mt-3 text-sm text-critical-text" dir="auto">
          {t('approval.failedBody')}
        </p>
      ) : (
        <>
          <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
            {rows
              .filter(([, v]) => Boolean(v))
              .map(([k, v]) => (
                <div key={k} className="contents">
                  <dt className="text-ink-2">{k}</dt>
                  <dd className="tabular text-ink [overflow-wrap:anywhere]" dir="auto">
                    {v}
                  </dd>
                </div>
              ))}
          </dl>
          {approval.preview ? (
            <div className="mt-3 rounded-md border border-line bg-canvas px-3 py-2">
              <p className="mb-1 text-xs font-medium text-ink-2">{t('approval.preview')}</p>
              <p
                className="whitespace-pre-wrap text-sm text-ink [overflow-wrap:anywhere]"
                dir="auto"
              >
                {preview}
              </p>
              {previewLong ? (
                <button
                  type="button"
                  onClick={() => setExpanded((v) => !v)}
                  className="mt-1 min-h-10 text-sm font-medium text-brand"
                >
                  {expanded ? t('approval.showLess') : t('approval.showAll')}
                </button>
              ) : null}
            </div>
          ) : null}
          {approval.why || approval.authority ? (
            <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 border-t border-line pt-3 text-sm">
              {approval.why ? (
                <div className="contents">
                  <dt className="text-ink-2">{t('approval.why')}</dt>
                  <dd className="text-ink" dir="auto">
                    {approval.why}
                  </dd>
                </div>
              ) : null}
              {approval.authority ? (
                <div className="contents">
                  <dt className="text-ink-2">{t('approval.authority')}</dt>
                  <dd className="text-ink" dir="auto">
                    {approval.authority}
                  </dd>
                </div>
              ) : null}
            </dl>
          ) : null}
          <div className="mt-4 flex gap-2">
            <Button
              className="flex-1"
              loading={busy || localBusy}
              onClick={() => {
                setLocalBusy(true);
                void Promise.resolve(onApprove()).finally(() => setLocalBusy(false));
              }}
            >
              <span className={amount ? 'tabular' : ''}>{primaryLabel}</span>
            </Button>
            <Button
              variant="secondary"
              className="flex-1"
              disabled={busy || localBusy}
              onClick={() => void onCancel()}
            >
              {t('approval.cancel')}
            </Button>
          </div>
        </>
      )}
    </section>
  );
}

export { formatMoney };
